import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InfoPage from '../InfoPage';
import { authFetch } from '../../utils/authFetch';
import { persistAuthResult } from '../../utils/authHelpers';
import { usePersona } from '../../contexts/PersonaContext';

const STEP_EMAIL = 'email';
const STEP_CODE = 'code';
const STEP_REVIEW = 'review';
const STEP_DONE = 'done';

const stepHeading = {
  [STEP_EMAIL]: 'Combine an existing Postaway account',
  [STEP_CODE]: 'Verify the other email',
  [STEP_REVIEW]: 'Review what will move',
  [STEP_DONE]: 'Accounts combined',
};

const CombineAccounts = () => {
  const navigate = useNavigate();
  const { refreshPersonaFromStorage } = usePersona();
  const [step, setStep] = useState(STEP_EMAIL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [devCode, setDevCode] = useState(null);
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [resultSummary, setResultSummary] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 767 : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Stack action buttons vertically (full width) on phones, in a row on
  // desktop. Reused across every step.
  const buttonRowStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? 8 : 12,
    width: '100%',
  };
  const buttonStyle = isMobile ? { width: '100%' } : undefined;
  const labelStyle = {
    display: 'block',
    color: '#0f084b',
    fontSize: isMobile ? 14 : undefined,
    fontWeight: 600,
  };
  // 16px keeps iOS Safari from auto-zooming when the field is focused.
  const inputBaseStyle = {
    display: 'block',
    width: '100%',
    padding: 12,
    marginTop: 6,
    borderRadius: 8,
    border: '1px solid #d6d8e0',
    fontSize: 16,
    boxSizing: 'border-box',
  };
  const cardStyle = {
    background: '#f6f7f9',
    padding: isMobile ? 14 : 16,
    borderRadius: 12,
  };
  const paragraphStyle = {
    color: '#0f084b',
    opacity: 0.75,
    margin: 0,
    fontSize: isMobile ? 14 : undefined,
    lineHeight: 1.5,
    wordBreak: 'break-word',
  };

  const reset = () => {
    setStep(STEP_EMAIL);
    setSecondaryEmail('');
    setRequestId(null);
    setCode('');
    setPreview(null);
    setResultSummary(null);
    setDevCode(null);
    setError('');
    setInfo('');
  };

  const sendCode = async () => {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const res = await authFetch('/api/persona/merge/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secondaryEmail: secondaryEmail.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        if (data.error === 'secondary_not_found') {
          throw new Error('No Postaway account was found for that email.');
        }
        if (data.error === 'secondary_already_merged') {
          throw new Error('That account has already been merged into another login.');
        }
        if (data.error === 'same_account') {
          throw new Error('That is your current login \u2014 enter the email of the OTHER account you want to combine.');
        }
        if (data.error === 'rate_limited') {
          throw new Error('Too many combine requests recently. Please try again in an hour.');
        }
        throw new Error(data.error || 'request_failed');
      }
      setRequestId(data.requestId);
      setDevCode(data.devCode || null);
      setStep(STEP_CODE);
      setInfo(
        data.devCode
          ? `Dev mode: your code is ${data.devCode}`
          : `We emailed a 6-digit code to ${secondaryEmail}. It expires in 15 minutes.`
      );
    } catch (e) {
      setError(e.message || 'Unable to start the combine flow.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const res = await authFetch('/api/persona/merge/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        if (data.error === 'invalid_code') {
          throw new Error(`That code didn't match. ${data.attemptsRemaining ?? 0} attempts remaining.`);
        }
        if (data.error === 'code_expired') {
          throw new Error('That code has expired. Start over to get a new one.');
        }
        if (data.error === 'too_many_attempts') {
          throw new Error('Too many failed attempts. Start over to get a new code.');
        }
        throw new Error(data.error || 'verify_failed');
      }
      setPreview(data.preview);
      setStep(STEP_REVIEW);
    } catch (e) {
      setError(e.message || 'Unable to verify code.');
    } finally {
      setBusy(false);
    }
  };

  const executeMerge = async () => {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const res = await authFetch('/api/persona/merge/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        if (data.error === 'merge_conflicts') {
          throw new Error('These accounts both have a profile of the same type and need an admin to resolve the conflict. Please contact support.');
        }
        throw new Error(data.error || 'merge_failed');
      }
      if (data.token || data.user) {
        persistAuthResult({
          email: data.user?.email,
          token: data.token,
          user: data.user,
        });
        refreshPersonaFromStorage();
      }
      setResultSummary(data);
      setStep(STEP_DONE);
    } catch (e) {
      setError(e.message || 'Unable to complete the merge.');
    } finally {
      setBusy(false);
    }
  };

  const renderEmailStep = () => (
    <div style={{ display: 'grid', gap: 16 }}>
      <p style={paragraphStyle}>
        Enter the email of an existing Postaway account you'd like to fold into your current login. We'll
        email a 6-digit code there to confirm you own it.
      </p>
      <label style={labelStyle}>
        Email of the other account
        <input
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={!isMobile}
          value={secondaryEmail}
          onChange={(e) => setSecondaryEmail(e.target.value)}
          placeholder="other@example.com"
          style={inputBaseStyle}
        />
      </label>
      <div style={buttonRowStyle}>
        <button
          className="button"
          type="button"
          disabled={busy || !secondaryEmail.includes('@')}
          onClick={sendCode}
          style={buttonStyle}
        >
          {busy ? 'Sending...' : 'Email verification code'}
        </button>
        <button
          type="button"
          className="button-stroke"
          onClick={() => navigate('/plan-settings')}
          style={buttonStyle}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderCodeStep = () => (
    <div style={{ display: 'grid', gap: 16 }}>
      <p style={paragraphStyle}>
        Enter the 6-digit code we sent to <strong>{secondaryEmail}</strong>.
      </p>
      <label style={labelStyle}>
        Verification code
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          autoFocus={!isMobile}
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          style={{
            ...inputBaseStyle,
            padding: isMobile ? '12px 10px' : '14px 12px',
            fontSize: isMobile ? 20 : 22,
            letterSpacing: isMobile ? 4 : 6,
            textAlign: 'center',
          }}
        />
      </label>
      {devCode && (
        <div style={{ color: '#b97400', fontSize: 13, wordBreak: 'break-all' }}>
          (Dev mode shortcut) Your code is <code>{devCode}</code>.
        </div>
      )}
      <div style={buttonRowStyle}>
        <button
          className="button"
          type="button"
          disabled={busy || code.length !== 6}
          onClick={verifyCode}
          style={buttonStyle}
        >
          {busy ? 'Verifying...' : 'Verify'}
        </button>
        <button
          type="button"
          className="button-stroke"
          disabled={busy}
          onClick={sendCode}
          style={buttonStyle}
        >
          Resend code
        </button>
        <button
          type="button"
          className="button-stroke"
          onClick={reset}
          style={buttonStyle}
        >
          Start over
        </button>
      </div>
    </div>
  );

  const renderReviewStep = () => {
    if (!preview) return null;
    const totalRows = (preview.rowCounts || []).reduce((sum, r) => sum + r.count, 0);
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <p style={paragraphStyle}>
          Here's what will move from <strong>{preview.secondary.email}</strong> onto{' '}
          <strong>{preview.primary.email}</strong>. After this completes, you'll log in only with{' '}
          <strong>{preview.primary.email}</strong>.
        </p>

        <div style={cardStyle}>
          <strong style={{ color: '#0f084b', fontSize: isMobile ? 14 : undefined }}>Roles to move</strong>
          <div style={{ color: '#0f084b', marginTop: 6, fontSize: isMobile ? 14 : undefined, wordBreak: 'break-word' }}>
            {preview.rolesToMove.length ? preview.rolesToMove.join(', ') : 'None'}
          </div>
        </div>

        <div style={cardStyle}>
          <strong style={{ color: '#0f084b', fontSize: isMobile ? 14 : undefined }}>Profiles to move</strong>
          <div style={{ color: '#0f084b', marginTop: 6, fontSize: isMobile ? 14 : undefined, wordBreak: 'break-word' }}>
            {preview.profileMoves.length
              ? preview.profileMoves.map((p) => p.table.replace('_profiles', '')).join(', ')
              : 'None'}
          </div>
          {(preview.emptyShellDrops || []).length > 0 && (
            <div style={{ color: '#0f084b', opacity: 0.65, marginTop: 8, fontSize: 13, lineHeight: 1.5 }}>
              Auto-cleanup: discarding empty{' '}
              {preview.emptyShellDrops
                .map((d) => `${d.table.replace('_profiles', '')} shell on ${d.side}`)
                .join(', ')}
              .
            </div>
          )}
        </div>

        <div style={cardStyle}>
          <strong style={{ color: '#0f084b', fontSize: isMobile ? 14 : undefined }}>Other records to move</strong>
          <div style={{ color: '#0f084b', marginTop: 6, fontSize: isMobile ? 14 : undefined, lineHeight: 1.5 }}>
            {totalRows} rows across {preview.rowCounts.length} tables (posts, properties, messages,
            subscriptions, assets, etc.)
          </div>
        </div>

        {preview.conflicts?.length > 0 && (
          <div
            style={{
              background: '#fff1f2',
              border: '1px solid #fecdd3',
              padding: isMobile ? 14 : 16,
              borderRadius: 12,
              color: '#dc1d08',
              fontSize: isMobile ? 14 : undefined,
              lineHeight: 1.5,
            }}
          >
            <strong>Conflicts to resolve</strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {preview.conflicts.map((c, i) => (
                <li key={i}>{c.message || c}</li>
              ))}
            </ul>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              Self-serve merge can't resolve these automatically. Please contact support to finish.
            </p>
          </div>
        )}

        <div style={buttonRowStyle}>
          <button
            className="button"
            type="button"
            disabled={busy || (preview.conflicts && preview.conflicts.length > 0)}
            onClick={executeMerge}
            style={buttonStyle}
          >
            {busy ? 'Combining...' : 'Combine accounts'}
          </button>
          <button
            type="button"
            className="button-stroke"
            onClick={reset}
            style={buttonStyle}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderDoneStep = () => (
    <div style={{ display: 'grid', gap: 16 }}>
      <p
        style={{
          color: '#0a8a3f',
          margin: 0,
          fontSize: isMobile ? 14 : undefined,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        Done! Your accounts are now combined under <strong>{resultSummary?.user?.email}</strong>.
      </p>
      <p style={paragraphStyle}>
        You now have these roles: {(resultSummary?.user?.roles || []).join(', ') || '—'}. Switch between
        personas using the menu in the header.
      </p>
      <div style={buttonRowStyle}>
        <button
          className="button"
          type="button"
          onClick={() => navigate('/dashboard')}
          style={buttonStyle}
        >
          Go to dashboard
        </button>
        <button
          type="button"
          className="button-stroke"
          onClick={() => navigate('/plan-settings')}
          style={buttonStyle}
        >
          Back to plan settings
        </button>
      </div>
    </div>
  );

  const stepLabels = [
    { key: STEP_EMAIL, num: '1', short: 'Email', full: 'Email' },
    { key: STEP_CODE, num: '2', short: 'Verify', full: 'Verify' },
    { key: STEP_REVIEW, num: '3', short: 'Review', full: 'Review' },
    { key: STEP_DONE, num: '4', short: 'Done', full: 'Done' },
  ];

  const below = (
    <div
      style={{
        width: '100%',
        maxWidth: 720,
        margin: isMobile ? '24px auto 48px' : '64px auto',
        padding: isMobile ? '0 16px' : '0 20px',
      }}
    >
      <h1
        style={{
          color: '#0f084b',
          marginBottom: 8,
          fontSize: isMobile ? 24 : undefined,
          lineHeight: isMobile ? 1.2 : undefined,
          wordBreak: 'break-word',
        }}
      >
        {stepHeading[step]}
      </h1>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isMobile ? 6 : 8,
          marginBottom: isMobile ? 16 : 24,
          color: '#6b7280',
          fontSize: isMobile ? 12 : 13,
        }}
      >
        {stepLabels.map((s, idx) => (
          <React.Fragment key={s.key}>
            <span style={{ fontWeight: step === s.key ? 700 : 400, color: step === s.key ? '#0f084b' : '#6b7280' }}>
              {s.num}. {isMobile ? s.short : s.full}
            </span>
            {idx < stepLabels.length - 1 && <span aria-hidden="true">›</span>}
          </React.Fragment>
        ))}
      </div>

      {info && (
        <div
          style={{
            color: '#0f084b',
            background: '#eef2ff',
            border: '1px solid #c7d2fe',
            padding: isMobile ? 10 : 12,
            borderRadius: 8,
            marginBottom: 16,
            fontSize: isMobile ? 14 : undefined,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {info}
        </div>
      )}
      {error && (
        <div
          style={{
            color: '#dc1d08',
            background: '#fff1f2',
            border: '1px solid #fecdd3',
            padding: isMobile ? 10 : 12,
            borderRadius: 8,
            marginBottom: 16,
            fontSize: isMobile ? 14 : undefined,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {error}
        </div>
      )}

      {step === STEP_EMAIL && renderEmailStep()}
      {step === STEP_CODE && renderCodeStep()}
      {step === STEP_REVIEW && renderReviewStep()}
      {step === STEP_DONE && renderDoneStep()}
    </div>
  );

  return <InfoPage title="" below={below} disableStamps />;
};

export default CombineAccounts;
